import { ArrowUpRight, X } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSTS = [
  {
    id: "training-before-lifting",
    title: "Training",
    excerpt:
      "Before the barbell, before the reps — you have to retrain the mind. The phoenix sheds first, then rises.",
    category: "Mindset",
    read: "5 min",
    // 🔥 Painterly phoenix rising from the ashes with a shadow-phoenix below —
    // owner drop Mar 5, 2026. Title reads: Training before Lifting.
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/9ksvnrom_1788815227773~2.jpg",
    imgFit: "cover",
    content: [
      { type: "lede", text: "It seems many don't talk about it — or skip right over the pre-training." },
      { type: "h", text: "So what do I mean by pre-training?" },
      { type: "p", text: "I mean allowing yourself to be validated, vulnerable, and open-minded to the possibility of a newer you, a newer body, and a newer mindset." },
      { type: "p", text: "A lot of the time, training stops — or stops working — because we overtax the brain. We overload it into wanting more before the foundation is even set." },
      { type: "h", text: "Know your real reason" },
      { type: "p", text: "You need to have the realistic reason why you are doing this. That reason should be because you care — because your health, mind, body, and soul want more than what you've been doing so far." },
      { type: "p", text: "Never make your health about others. Know and remember to make it because you love yourself enough to be and stay healthy. You are worthy of feeling good and admiring yourself for the actions you take for yourself." },
      { type: "h", text: "Training is replacement" },
      { type: "p", text: "Training is, in so many ways, replacing negative habits with positive thoughts and changes. Because over time you have trained yourself to be okay with a body or mindset you are unhappy with." },
      { type: "p", text: "Sometimes training is starting to retrain the mind and body for healthier food choices and the longevity of a long, healthy life — and hopefully being an inspiration to those who see you, your fight for a healthier you, and your personal journey through it all." },
      { type: "pullquote", text: "The phoenix sheds its old self to become a more beautiful version of itself — not only for self-love, but to show a majestic authority over its health." },
      { type: "kicker", text: "Flying Higher · Train better — but mindset is trained first 🐦‍🔥👀💜☯️💯 omnimuscleholistichealing.com" },
    ],
  },
  {
    id: "nesting-personal-evolution",
    title: "Nesting of Personal Evolution",
    excerpt:
      "Nesting isn't settling — it's the phoenix ritual of gathering, burning, and rebuilding into the self you're still becoming.",
    category: "Mindset",
    read: "5 min",
    // 🔥 Painterly phoenix-rising-from-a-branch nest — owner drop Mar 5, 2026.
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/fynm0lxz_1788641962837.png",
    imgFit: "cover",
    content: [
      { type: "lede", text: "Nesting… it's not necessarily settling in or settling down. Not accepting things as they are. Not even just using what is there." },
      { type: "p", text: "People, places, and things change in big and little ways even when we aren't aware of it or can't see the growth. And yes — growth can be exhausting, especially when those people, places, and things grow negatively or in ways we don't want. Usually we become accustomed to the way things were, and we accept that as truth." },
      { type: "p", text: "When we think of nesting we usually think of disorganization being organized into what is already there. More times than not, open-mindedness toward growth or change is beyond our capability to understand, because our normal comfort has been our normal for so long. Any variable that doesn't conform to what we're used to becomes a movement we can't accept — and usually don't or won't." },
      { type: "h", text: "Give Room for Growth — Yours and Theirs" },
      { type: "p", text: "Give room not only for your own self-improvement, but for others as well. Learning more, adapting, and adding new information to your mind gives better understanding, better decisions, and better overall health. More information always leads to better-informed feedback and better calls. Nesting is using what is there, gathering more, and making it work — to build not only comfort, but the presentation you want to be, and to be part of." },
      { type: "h", text: "The Phoenix Ritual" },
      { type: "p", text: "Birds — especially phoenixes — search and gather wood, twigs, branches, and fruits to make their nest comfortable, even if at some point they burn it all down. The burning is only part of the ritual: new life, resetting, and the renewal of a new self." },
      { type: "p", text: "The new self usually wants and needs a different kind of nesting. This new nest usually carries very little of the old self — the old people, places, and things — but only because it's not the same in so many ways. It's an evolution of oneself, accepting the old alongside the new. Accepting the old and welcoming the new with open-mindedness and room for growth — hopefully on a positive path — allows the current nest to remain substantially sustainable until the next evolution, and the next nest." },
      { type: "pullquote", text: "We never lose our past self — we gain more knowledge of the whole we are still learning, wrapped in life lessons and paths of evolution and self-growth… if we allow it." },
      { type: "kicker", text: "Flying Higher to inspire positive change ☯️🐦‍🔥 omnimuscleholistichealing.com" },
    ],
  },
  {
    id: "decluttering-mind-body-soul",
    title: "The Hidden Cost of Micro-Friction: Decluttering for Mind, Body, and Soul",
    excerpt:
      "Clutter isn't a minor annoyance — it's a quiet micro-friction that sabotages your health, momentum, and peace of mind.",
    category: "Mind, Body, Soul",
    read: "5 min",
    // 🔥 Phoenix graphic — owner drop for the Decluttering article.
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/9obqyohq_1788315346385~2.jpg",
    imgFit: "contain",
    content: [
      { type: "lede", text: "Clutter is such an easy thing to overlook, but it can disrupt and disorganize not only our physical environment but our mental and emotional space as well." },
      { type: "p", text: "We often think of disorganization as a minor inconvenience—a misplaced set of keys, a missing gym towel, or a frantic scramble out the door five minutes late. We shrug them off as daily quirks. But when you look closer, these aren't just harmless annoyances. They are micro-frictions that quietly sabotage your health, your momentum, and your peace of mind." },
      { type: "p", text: "The body and brain function far more smoothly and naturally when they are not constantly held back by lingering, avoidable miniature tasks born of disorder." },
      { type: "h", text: "The Toll on Your Mind and Nervous System" },
      { type: "p", text: "When your environment is disorganized, your brain treats every stray item and misplaced object as background noise requiring low-grade processing." },
      { type: "list", items: [
        { label: "Cognitive Fatigue", text: "Searching high and low for a piece of home gym equipment or your favorite training gear triggers an immediate spike of frustration. That unexpected mental speed bump burns valuable cognitive energy before your workout even begins." },
        { label: "The Stress Cascade", text: "These constant minor delays keep your nervous system in a state of subtle, continuous tension. Instead of flowing naturally into your day, you start on the defensive—rushing, reacting, and playing catch-up." },
      ]},
      { type: "h", text: "The Ripple Effect on Physical Well-Being and Limitations" },
      { type: "p", text: "Physical training, holistic health, and daily wellness rely heavily on rhythm, timing, and intentional habits. Disorganization disrupts that flow in very tangible ways:" },
      { type: "list", items: [
        { label: "Compromised Performance", text: "If hunting down your gear makes you late, your pre-workout routine gets compressed. You miss your warm-up window, rush your mindset, or skip your preparation habits that prime your body for peak performance." },
        { label: "The Energy Leak", text: "Dealing with avoidable chaos drains the enthusiasm you need to push through a tough conditioning session or stay consistent with your long-term wellness goals." },
      ]},
      { type: "h", text: "Practical Steps to Reset" },
      { type: "p", text: "You don't have to tackle your entire living or working space all at once. Small, sustainable habits yield the most powerful long-term results:" },
      { type: "list", items: [
        { label: "Start Small", text: "Pick a single drawer, a single shelf, or a five-foot radius to clear out today." },
        { label: "Create Zones", text: "Keep surfaces that you use daily—like your kitchen counters or your workspace—completely clear of random items." },
        { label: "Embrace the Daily Sweep", text: "Spend just 10 minutes at the end of the day putting things back in their proper place so you wake up to clarity, not chaos." },
      ]},
      { type: "kicker", text: "True Vitality: Balancing Mind, Body, and Soul in Daily Life" },
    ],
  },
  {
    id: "breaking-through-the-wall",
    title: "Breaking Through the Wall: Patience, Unlearning, and Self-Love",
    excerpt:
      "The three traps that stall every goal — Want, Need, Ego — and the patient, self-loving way through the wall.",
    category: "Mindset",
    read: "6 min",
    // 🔥 Phoenix graphic — dedicated OMH "Breaking Through the Wall"
    // infographic supplied by owner Sep 2, 2026.
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/tbkr9a2w_1787857930884.jpg",
    imgFit: "contain",
    content: [
      { type: "lede", text: "One of the main reasons we get lost in our short- and long-term goals isn't a lack of discipline — it's the stories we tell ourselves." },
      { type: "p", text: "We get lost because of self-justification, driven by the top three traps: \"I want,\" \"I need,\" and \"Ego.\"" },
      { type: "list", items: [
        { label: "\"I want\"", text: "ties us to fleeting impulses, chasing temporary moods or measuring our worth against someone else's highlight reel (\"I want a good life because someone else seems to have it better\")." },
        { label: "\"I need\"", text: "turns growth into a heavy, transactional obligation (\"I need to do this so I can finally get what I want\")." },
        { label: "Ego", text: "builds the ultimate defense mechanism and the illusion of control, whispering the most dangerous lie of all: \"I can stop anytime I choose… I just don't have to do it right now.\"" },
      ]},
      { type: "p", text: "Eventually, these traps lead us straight into a wall. And when you hit that wall, forcing your way through with more ego or pressure doesn't work." },
      { type: "h", text: "A New Way Forward: Patience, Unlearning, and Support" },
      { type: "p", text: "Real, sustainable progress doesn't come from burnout or forced hustle. It requires a completely different approach:" },
      { type: "list", items: [
        { label: "Locate the Wall with Compassion", text: "Take the time to research the most realistic, reasonable way forward without judging yourself for getting stuck." },
        { label: "Lean on Your Circle", text: "Realize you don't have to carry it all alone. Involve friends, family, or a therapist — outside help is a strength, not a weakness." },
        { label: "Embrace Slow Discomfort", text: "Give yourself permission to do things slowly, stepping just outside your comfort zone one small, deliberate action at a time." },
        { label: "Mix Wisdom with an Open Mind", text: "Not everything works for everyone, but all things take time to learn and unlearn. Don't get distracted or discouraged just because the first couple of things don't work out for you right away." },
      ]},
      { type: "h", text: "The Real Purpose: Sustainable Improvement" },
      { type: "p", text: "True transformation happens when we drop the noise of want, need, and ego. Our daily actions should be driven by a much simpler, enduring mission: to improve ourselves and our lives, both in our day-to-day routines and across our lifetime." },
      { type: "pullquote", text: "Love yourself enough to love yourself while you are trying to better you." },
      { type: "p", text: "When daily progress is rooted in self-respect — rather than a race against someone else or a battle with your own ego — consistency stops feeling like a struggle." },
      { type: "h", text: "Key Pillars for Your Journey" },
      { type: "list", items: [
        { label: "Catch the Ego's Lies", text: "Recognize when you're telling yourself you can quit later, or running a race against someone else's timeline." },
        { label: "Honor the Process", text: "Pause when you hit a wall, accept that slowing down is required, and give yourself grace while you unlearn old patterns." },
        { label: "Build Your Support System", text: "Rely on trusted friends, family, or mentors to help guide you through the heavy spots." },
        { label: "Build for a Lifetime", text: "Design your daily routines to nourish your body and mind today, taking steady, imperfect steps toward long-term growth." },
      ]},
      { type: "kicker", text: "Flying Higher to inspire positive change ☯️🐦‍🔥 omnimuscleholistichealing.com" },
    ],
  },
  {
    id: "omnimuscle-workout-routine",
    title: "My OmniMuscle Workout Routine 💪🏾",
    excerpt:
      "Bottom-half heavy — leg day twice a week. Plus the supplements I run from Blackstone Labs (code OMNIMUSCLE).",
    category: "Training",
    read: "5 min",
    // 🔥 Dedicated OmniMuscle Workout Routine phoenix infographic
    // (fire + water dual phoenix). Owner drop Sep 2, 2026.
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/5t8gcuja_FB_IMG_1788328731216.jpg",
    imgFit: "contain",
    content: [
      { type: "lede", text: "Bottom-half heavy 😅 — and I have ZERO regrets. Here's the exact split, the exact list, and the supplements I run." },
      { type: "p", text: "For everyone asking about my normal workout routine and Blackstone Labs supplements — this is it. The schedule, the lifts, the reps, and the reminders that keep the joints happy while the muscle keeps coming. Use it as a template, not a sermon — your body, your call." },
      { type: "h", text: "📆 Weekly Split" },
      { type: "list", items: [
        { label: "Monday",    text: "Leg Day" },
        { label: "Tuesday",   text: "Upper Body" },
        { label: "Wednesday", text: "REST" },
        { label: "Thursday",  text: "Leg Day" },
        { label: "Friday",    text: "Upper Body" },
        { label: "Saturday",  text: "Full Body & Focus" },
        { label: "Sunday",    text: "REST" },
        { label: "Daily Cardio", text: "25-minute walk — every day, no exceptions." },
      ]},
      { type: "h", text: "🦵🏾 Leg Day — The OmniMuscle List" },
      { type: "h3", text: "1. Goblet Squats" },
      { type: "p", text: "3 sets × 10-12 reps. Maintain safety and prioritize depth to target the quads. The squat is the test — keep your chest tall, sit BETWEEN your knees, not in front of them." },
      { type: "h3", text: "2. Seated Leg Extensions (my favorite)" },
      { type: "p", text: "3 sets × 12-15 reps. Squeeze and hold the contraction at the top of each rep for maximum tension. The 1-second pause at the top is where the magic lives." },
      { type: "h3", text: "3. Romanian Deadlifts (RDLs)" },
      { type: "p", text: "3 sets × 10 reps. Barbell or dumbbells — your call. Focus heavily on the glute and hamstring stretch. If you don't feel it in the back of your legs, you're not going low enough." },
      { type: "h3", text: "4. Seated Calf Raises" },
      { type: "p", text: "3 sets × 20 reps. Pause clearly at the bottom stretch to build lower leg mass. Calves are stubborn — they live on the bottom of the rep, not the top." },
      { type: "h3", text: "5. Cable Ab Crunches" },
      { type: "p", text: "3 sets × 15 reps. Lock in your posture and use the weight stack to crunch downward. Your abs are not arms — keep the pull through the midsection." },
      { type: "h", text: "💪🏾 Upper Body — The OmniMuscle List" },
      { type: "h3", text: "6. Machine Chest Press" },
      { type: "p", text: "3 sets × 8-12 reps. Stable, fixed-motion press to safely overload the chest. The machine takes the guesswork out so you can push harder without form drift." },
      { type: "h3", text: "7. Machine Pec Flyes" },
      { type: "p", text: "3 sets × 12-15 reps. Keep a slight bend in the elbows and focus on a deep chest stretch. Don't slam the handles — let the stretch do the work." },
      { type: "h3", text: "8. Lat Pulldowns (my favorite)" },
      { type: "p", text: "3 sets × 10-12 reps. Drive your elbows down toward your hips to build back width. The lats grow when you THINK about the elbows, not the bar." },
      { type: "h3", text: "9. EZ-Bar Bicep Curls" },
      { type: "p", text: "3 sets × 10-12 reps. Use a controlled motion; the angled bar keeps the wrists in a safe, comfortable position. Slow eccentrics, no swinging, full range." },
      { type: "h", text: "⚓️ Core Execution Reminder" },
      { type: "pullquote", text: "Move with total deliberation. Control the negative portion of every single rep to maximize muscle growth while keeping your joints completely protected." },
      { type: "h", text: "🐦‍🔥 Stack — What I Run" },
      { type: "p", text: "I run @blackstone_labs supplements alongside this routine. Use code OMNIMUSCLE at checkout on blackstonelabs.com:" },
      { type: "list", items: [
        { label: "Halo Elite",    text: "Strength + lean mass driver — my anchor for the heavy days." },
        { label: "Isolation",     text: "Clean protein — fast, light, and easy on digestion." },
        { label: "EAA",           text: "Essential Amino Acids — keeps me in growth mode between meals." },
        { label: "Dust-X",        text: "Pre-workout — focus and energy without the crash." },
        { label: "HYPE",          text: "Pump + drive — the legs feel everything on HYPE days." },
      ]},
      { type: "kicker", text: "Helping this Phoenix 🐦‍🔥 rise. omnimuscleholistichealing.com" },
    ],
  },
  {
    id: "sleep-phoenix",
    title: "Sleep 😴 🐦‍🔥",
    excerpt:
      "From the ashes of a grueling day, we do not simply wake up — we rise. Every night, the human body lives the phoenix myth.",
    category: "Recovery",
    read: "7 min",
    // 🔥 Phoenix in jeweled nest above embers — dedicated Sleep article
    // graphic. Owner drop Sep 2, 2026.
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/x62saaz4_1781244815053.png",
    imgFit: "contain",
    content: [
      { type: "lede", text: "From the ashes of a grueling day, we do not simply wake up — we rise." },
      { type: "p", text: "Throughout history, human culture has been obsessed with the myth of the phoenix, the legendary bird that burns to ash at the end of its life cycle only to emerge from the embers, reborn and magnificent. We treat it as a fantasy. Yet, every single night, the human body undergoes a deeply biological version of this exact cycle." },
      { type: "p", text: "We live in a culture that treats sleep like a luxury or, worse, a concession to weakness. We wear our sleep deprivation like a badge of honor, burning our candles at both ends. But without the ash, there is no rebirth. Adequate sleep is not a passive pause in your day; it is the vital, fiery crucible of your physical and mental recovery." },
      { type: "h", text: "The Evening Conflagration: Burning to Ash" },
      { type: "p", text: "By the time evening falls, your body and mind have spent fifteen or sixteen hours accumulating damage. Every stressor, every physical exertion, and every complex cognitive task leaves behind a biological residue." },
      { type: "list", items: [
        { label: "Metabolic Waste", text: "Throughout the day, your brain's intense activity builds up cellular waste products, including amyloid-beta proteins — the same plaques associated with neurodegenerative diseases." },
        { label: "Micro-Tears and Inflammation", text: "Your muscles experience microscopic tears from movement, and your immune system battles daily oxidative stress." },
      ]},
      { type: "p", text: "By midnight, you are structurally and mentally spent. You are the aging phoenix, weighed down by the heavy, frayed feathers of the day's demands. If you try to bypass the sleep cycle — forcing yourself to keep flying on empty — the feathers simply decay further. You don't get to skip the destruction phase; you just end up flying as a creature made of smoke and exhaustion. To truly reset, you have to surrender to the night. You have to let the day burn out." },
      { type: "h", text: "The Midnight Crucible: Rebuilding from the Embers" },
      { type: "p", text: "When your eyes close and you drift into deep sleep, the biological fires ignite. Far from being a state of \"nothingness,\" sleep is an incredibly active, highly orchestrated phase of cellular alchemy. This is where the true phoenix transformation happens, divided into two primary restorative phases:" },
      { type: "h3", text: "1. The Physical Forge (Deep NREM Sleep)" },
      { type: "p", text: "During non-rapid eye movement (NREM) sleep, your blood pressure drops, and your breathing slows. Your body channels its energy away from the external world and directs it entirely inward. The pituitary gland releases a massive surge of Human Growth Hormone (HGH). This hormone acts as the ultimate cosmic repair technician, rushing to heal those micro-tears in your muscles, synthesizing proteins, and regenerating tissues. Your immune system releases cytokines, small proteins that target inflammation and defend against infection." },
      { type: "h3", text: "2. The Mental Alchemist (REM Sleep)" },
      { type: "p", text: "While your body is being rebuilt, your brain is undergoing its own intense restoration. During rapid eye movement (REM) sleep, your brain's glymphatic system — essentially its internal waste-clearance mechanism — opens up. It flushes out those toxic metabolic wastes accumulated during the day, quite literally washing the brain clean. Simultaneously, your hippocampus and neocortex work together to catalog memories, process emotions, and strip away the painful sting of stressful experiences." },
      { type: "p", text: "Without this nightly crucible, the repair work is left unfinished. Your biological infrastructure remains fractured, your brain remains cluttered with cognitive debris, and you wake up carrying the weight of yesterday's ashes into today." },
      { type: "h", text: "The Morning Flight: Emerging Radiant" },
      { type: "p", text: "Adequate sleep — defined for most adults as seven to nine hours of uninterrupted rest — is what allows the cycle to complete cleanly. When you get enough sleep, you do not just wake up feeling \"less tired.\" You emerge as a fundamentally updated version of yourself. Your cognitive processing is sharper, your emotional baseline is steadier, your immune defense is fortified, and your muscles are primed for effort." },
      { type: "pullquote", text: "The Lesson of the Phoenix: True strength isn't about avoiding the burnout; it's about honoring the recovery." },
      { type: "p", text: "Protecting your sleep isn't an act of indulgence. It is a fierce, non-negotiable boundary that guards your health, your longevity, and your vitality. If you want to burn brightly and intensely during the day, you must give yourself permission to dissolve into the restorative shadows of the night." },
      { type: "kicker", text: "Let the day turn to ash. Close your eyes, trust the process, and prepare to rise." },
    ],
  },
  {
    id: "healthy-boundaries",
    title: "Healthy Boundaries: Find Your Grounding & Ascend",
    excerpt:
      "Boundaries aren't about right or wrong — they're the quiet grounding that keeps you flying higher when the world tries to pull you back down.",
    category: "Mind, Body, Soul",
    read: "6 min",
    // 🔥 Phoenix rising with "MORALS & VALUES" shield — dedicated
    // "Healthy Boundaries" article graphic. Owner drop Sep 3, 2026.
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/vjnrg6xf_1788448146667~2.jpg",
    imgFit: "contain",
    content: [
      { type: "lede", text: "Healthy boundaries allow us to keep our morals and values in check when we feel unsure or frustrated — keeping us grounded in our beliefs." },
      { type: "p", text: "It's usually not about right or wrong, but being mindful of what is said and done in our presence. It's also about the point of view shaped by our standards and stance — how we see the world, and how our own experiences define our understanding of the universe." },
      { type: "p", text: "Please understand that knowing this is not the absolute gospel. Allowing some room for understanding and staying open-minded lets us expand our horizons mentally, giving us the knowledge to foster psychological evolution. No one knows it all, and pretending to be all-knowing leaves zero room to learn from those who know something you don't." },
      { type: "pullquote", text: "Once you know better, you do better… hopefully." },
      { type: "h", text: "When Others Push Back on Your Boundaries" },
      { type: "p", text: "Another culprit — sometimes, or even more often than not — is the people who try to make you feel bad about yourself simply because you have healthy boundaries. Unfortunately, most people don't have healthy boundaries, and they have meltdowns and lash out in frustration. This happens for many reasons, but usually because they were never taught how to deal with boundaries or respond to them in a safe, adult manner." },
      { type: "list", items: [
        { label: "They Weren't Taught", text: "Many were never shown what a safe adult response to a boundary looks like." },
        { label: "Enabled Behavior", text: "Others were enabled and had their bad behavior fed over time until it became their norm — and unlearning bad behavior is usually a lingering issue." },
        { label: "Missing Tools", text: "And many simply lack the coping mechanisms or healthier strategies to reach a different, improved outcome." },
      ]},
      { type: "h", text: "Self-Responsibility Is a Form of Healthy Boundary" },
      { type: "p", text: "A lot of the time, we view self-responsibility as \"fault\" — but just like setting boundaries, it's not about fault at all. Fault implies a purposely wrongful act or a bad response to an undesirable situation. We confuse it with blame, liability, defects, or character flaws — but how can you truly be at fault if you don't understand what you are doing?" },
      { type: "p", text: "Sure, most people know basic right from wrong, but even that ties directly back to our point of view, our individual perception, and how we uniquely see and interpret the world." },
      { type: "h", text: "Meeting in the Middle" },
      { type: "p", text: "Ultimately, self-responsibility and boundaries help us connect with everyone involved so we can meet in the middle, respect one another, and work through misunderstandings. Ideally, this opens the door to healthy dialogue and prevents future conflict — avoiding harmful words and actions so, with any luck, it doesn't happen again." },
      { type: "pullquote", text: "Never let anyone make you feel bad for having healthy boundaries, respecting yourself, or honoring your thoughts and experiences." },
      { type: "p", text: "Always leave room for open-mindedness and growth, and always be your own self-advocate." },
      { type: "h", text: "Protect Your Mental Health First" },
      { type: "p", text: "If worst comes to worst, it is better to change the subject or remove yourself from the situation altogether. Always protect your mental health before anything else — and know that doing so is completely okay." },
      { type: "pullquote", text: "Flying higher above the negative to inspire positive change is far better than falling into the quicksand of drama and trauma." },
      { type: "p", text: "Healthy boundaries bring mental, emotional, psychological, physical, and social balance to yourself, while fostering genuine respect and understanding with those around you." },
      { type: "kicker", text: "Find your grounding. Then ascend. 🐦‍🔥 omnimuscleholistichealing.com" },
    ],
  },
];

function ArticleModal({ post, onClose }) {
  // Lock body scroll ONLY while the reader is actually open
  useEffect(() => {
    if (!post) return; // do nothing when there's no article to render
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [post, onClose]);

  if (!post) return null;
  return (
    <div
      data-testid={`article-modal-${post.id}`}
      className="fixed inset-0 z-[60] overflow-y-auto bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="min-h-full flex justify-center px-4 py-10 sm:py-16">
        <article
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl bg-gradient-to-b from-zinc-950 to-black border border-[#D4AF37]/20 shadow-[0_0_60px_rgba(212,175,55,0.1)]"
        >
          <button
            data-testid={`article-modal-close-${post.id}`}
            onClick={onClose}
            className="sticky top-4 left-full -translate-x-12 z-10 p-2 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-white hover:border-[#D4AF37] transition"
            aria-label="Close article"
          >
            <X size={18} />
          </button>

          <div className="aspect-[16/8] overflow-hidden border-b border-zinc-800 -mt-10 bg-gradient-to-br from-[#1a0030] via-black to-[#2a0010] flex items-center justify-center">
            <img
              src={post.img}
              alt={post.title}
              className={`w-full h-full ${post.imgFit === "contain" ? "object-contain p-6" : "object-cover opacity-80"}`}
            />
          </div>

          <div className="px-6 sm:px-12 py-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">
                {post.category}
              </span>
              <span className="text-xs text-neutral-500">{post.read} read</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl text-white leading-[1.05] mb-8">
              {post.title}
            </h1>

            <div className="prose-article text-neutral-300">
              {post.content.map((block, i) => {
                if (block.type === "lede") {
                  return (
                    <p key={i} className="text-xl sm:text-2xl text-white font-display leading-snug mb-8 border-l-2 border-[#DC2626] pl-5">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === "h") {
                  return (
                    <h2 key={i} className="font-display text-2xl sm:text-3xl text-[#D4AF37] mt-10 mb-4 leading-tight">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "h3") {
                  return (
                    <h3 key={i} className="font-display text-xl text-white mt-7 mb-3">
                      {block.text}
                    </h3>
                  );
                }
                if (block.type === "list") {
                  return (
                    <ul key={i} className="space-y-3 mb-6 ml-1">
                      {block.items.map((it, j) => (
                        <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-neutral-300">
                          <span className="text-[#DC2626] mt-1.5 shrink-0">▸</span>
                          <span>
                            <strong className="text-white font-semibold">{it.label}:</strong>{" "}
                            <span className="text-neutral-400">{it.text}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === "pullquote") {
                  return (
                    <blockquote key={i} className="my-10 py-6 px-6 border-y border-[#D4AF37]/30 text-center">
                      <p className="font-display text-xl sm:text-2xl text-[#D4AF37] italic leading-snug">
                        {block.text}
                      </p>
                    </blockquote>
                  );
                }
                if (block.type === "kicker") {
                  return (
                    <p key={i} className="text-lg sm:text-xl text-white font-display italic leading-snug mt-10 pt-6 border-t border-zinc-800">
                      {block.text}
                    </p>
                  );
                }
                return (
                  <p key={i} className="text-[15px] sm:text-base leading-[1.75] text-neutral-300 mb-5">
                    {block.text}
                  </p>
                );
              })}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

export default function Blog() {
  const [openPost, setOpenPost] = useState(null);

  return (
    <section id="blog" data-testid="blog-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <div className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.3em] mb-3">
              The Journal
            </div>
            <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.9]">
              Field Notes
              <br />
              From The <span className="text-[#DC2626]">Trenches.</span>
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {POSTS.map((post) => {
            const isReadable = Array.isArray(post.content) && post.content.length > 0;
            return (
              <article
                key={post.id}
                data-testid={`blog-card-${post.id}`}
                onClick={() => isReadable && setOpenPost(post)}
                className={`group ${isReadable ? "cursor-pointer" : "cursor-default opacity-90"}`}
              >
                <div className="aspect-[4/3] overflow-hidden mb-5 border border-zinc-800">
                  <img
                    src={post.img}
                    alt={post.title}
                    className="w-full h-full object-cover bg-black group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">
                    {post.category}
                  </span>
                  <span className="text-xs text-neutral-500">{post.read} read</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-3 group-hover:text-[#D4AF37] transition-colors">
                  {post.title}
                </h3>
                <p className="text-neutral-400 text-sm leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                <span
                  data-testid={`blog-card-cta-${post.id}`}
                  className={`inline-flex items-center gap-1 text-sm uppercase tracking-widest font-semibold ${
                    isReadable ? "text-white group-hover:text-[#D4AF37]" : "text-neutral-600"
                  }`}
                >
                  {isReadable ? "Read" : "Coming soon"}
                  {isReadable && <ArrowUpRight size={16} />}
                </span>
              </article>
            );
          })}
        </div>
      </div>

      {/* 📬 Newsletter Signup — Mar 5, 2026 */}
      <NewsletterSignup />

      <ArticleModal post={openPost} onClose={() => setOpenPost(null)} />
    </section>
  );
}


/* ──────────────────────────────────────────────────────────────
 * 📬 NewsletterSignup — sits under the Journal grid. Emails the
 * subscriber a branded welcome via Resend on the first signup.
 * ────────────────────────────────────────────────────────────── */
function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const val = (email || "").trim();
    if (!val || !val.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/newsletter/subscribe`, {
        email: val,
        source: "journal-footer",
      });
      if (r.data?.already_subscribed) {
        toast.success("📬 You're already on the list. Fly higher.");
      } else {
        toast.success("📬 Welcome to the Journal — check your inbox.");
      }
      setDone(true);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (Array.isArray(detail) && detail[0]?.msg) || "Couldn't subscribe — please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 md:mt-24">
      <div
        className="relative overflow-hidden border border-[#D4AF37]/40 bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#0a0a0a] p-8 md:p-12"
        data-testid="newsletter-signup"
      >
        {/* accent bar */}
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#4C1D95] via-[#D4AF37] to-[#DC2626]" />

        <div className="grid md:grid-cols-[1.2fr_1fr] gap-8 md:gap-14 items-center">
          <div>
            <div className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.35em] mb-3">
              📬 Get New Articles by Email
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white leading-[1.05] mb-3">
              Real notes. <span className="text-[#D4AF37]">Zero noise.</span>
            </h3>
            <p className="text-neutral-400 text-sm md:text-base leading-relaxed max-w-xl">
              One email when a new Journal entry drops — training, cooking, mindset, healing.
              No hustle-culture sales pitches. Unsubscribe anytime.
            </p>
          </div>

          <form onSubmit={onSubmit} className="w-full" data-testid="newsletter-form">
            <label htmlFor="newsletter-email" className="sr-only">Email</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={busy}
                data-testid="newsletter-email-input"
                className="flex-1 min-w-0 bg-black/60 border border-zinc-700 focus:border-[#D4AF37] outline-none text-white text-sm px-4 py-3 tracking-wide disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit-btn"
                className="shrink-0 bg-[#D4AF37] hover:bg-[#e0bc4a] text-black font-black uppercase tracking-[0.2em] text-xs px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? "Sending…" : done ? "Subscribed ✓" : "Subscribe"}
              </button>
            </div>
            <p className="text-[10px] text-neutral-600 mt-3 tracking-wide">
              We'll only email you when there's something worth reading.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
